import React from "react";
import { HoverRows } from "./GeotabHover.jsx";
import GeotabBadge from "./GeotabBadge.jsx";
import MapLink from "./GeotabMapLink.jsx";
import {
  DELIVERY_FILL, DELIVERY_INK, DEPOT_COLOR, LOADING_FILL, LOADING_INK, OFF_COLOR, OUTLINE,
  ADDRESS_COLOR, deltaTone, fmtDelta, fmtDistance, fmtDuration, fmtTime, lineColor, placeRow, siteKindLabel,
} from "./geotabFormat.js";

const LABEL_WIDTH = 64;
const ROW_HEIGHT = 26;
const PLAN_Y = 20;
const REAL_Y = 76;
const HEIGHT = 108;
const MATCH_TEXT = { ok: "Trouvé", address: "Adresse à vérifier", place: "Lieu différent", missing: "Non détecté" };

const Hover = HoverRows;

export const deltaBadge = (minutes, tolerance) => <span className="inline-block w-16 align-middle"><GeotabBadge tone={deltaTone(minutes, tolerance)}>{fmtDelta(minutes)}</GeotabBadge></span>;

export function siteRows(site, s, tolerance) {
  const found = s?.real_arrival != null;
  return [
    ["Type", `${siteKindLabel(site.kind)}${site.kind !== "depot" ? ` · ${Math.round((site.tons || 0) * 10) / 10} t · ${site.order_count || 0} cmd` : ""}`],
    ["Prévu", `${fmtTime(site.arrival)} → ${fmtTime(site.departure)} · ${fmtDuration(site.departure - site.arrival)}`],
    s && ["Réel", !found ? "—" : s.open_end ? `${fmtTime(s.real_arrival)} → durée inconnue`
      : `${fmtTime(s.real_arrival)} → ${fmtTime(s.real_departure)} · ${s.short ? "arrêt court" : s.shared ? "livré et chargé au même arrêt" : fmtDuration(s.real_dwell)}`],
    found && ["Arrivée", deltaBadge(s.real_arrival - s.plan_arrival, tolerance)],
    s?.real_dwell != null && ["Sur place", deltaBadge(s.real_dwell - s.plan_dwell, tolerance)],
    s && ["Geotab", `${MATCH_TEXT[s.match] || "—"}${s.offset_m > 300 ? ` · ${fmtDistance(s.offset_m)}` : ""}`],
  ];
}
const COLORS = {
  loading: [LOADING_FILL, LOADING_INK],
  delivery: [DELIVERY_FILL, DELIVERY_INK],
  depot: [DEPOT_COLOR, "#fff"],
};

function Block({ x, y, width, kind, label, hover, dashed, stroke }) {
  const [fill, ink] = COLORS[kind] || COLORS.depot;
  const clipId = React.useId();
  const text = width > 70 ? label : width > 16 ? label.split(" · ")[0] : "";
  return (
    <Hover {...hover}>
      <g className="cursor-default">
        <rect x={x} y={y} width={width} height={ROW_HEIGHT} rx={3} fill={fill} stroke={stroke || OUTLINE}
          strokeWidth={dashed ? 2 : 1} strokeDasharray={dashed ? "4 2" : undefined} />
        <clipPath id={clipId}><rect x={x} y={y} width={Math.max(width - 3, 0)} height={ROW_HEIGHT} /></clipPath>
        <text clipPath={`url(#${clipId})`} x={x + 4} y={y + 17} fontSize={11} fontWeight={700} fill={ink}>{text}</text>
      </g>
    </Hover>
  );
}

function Marker({ x, y, fill, title, time }) {
  return <Hover title={title} rows={[["Heure", fmtTime(time)]]}><rect x={x - 3} y={y + 4} width={6} height={18} fill={fill} /></Hover>;
}

// Deux lignes sur le même axe : le plan du Dispatch au-dessus, les arrêts Geotab en dessous.
export default function GeotabGantt({ row, tolerance }) {
  const boxRef = React.useRef(null);
  const [width, setWidth] = React.useState(900);
  React.useEffect(() => {
    if (!boxRef.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(400, entry.contentRect.width)));
    observer.observe(boxRef.current);
    return () => observer.disconnect();
  }, []);

  const plan = row.plan;
  const cmp = row.comparison;
  const start = Math.floor(Math.min(plan.departure, cmp?.real_departure ?? plan.departure) / 60) * 60;
  const end = Math.ceil(Math.max(plan.return_at, cmp?.end_at ?? plan.return_at) / 60) * 60 + 30;
  const x = (m) => LABEL_WIDTH + ((m - start) / (end - start)) * (width - LABEL_WIDTH - 8);
  const hours = [];
  for (let m = start; m <= end; m += 60) hours.push(m);
  const sitesByIdx = Object.fromEntries((cmp?.sites || []).map((s) => [s.idx, s]));

  // Largeur en % : le SVG ne retient jamais la page plus large que la fenêtre (sinon défilement horizontal).
  return (
    <div ref={boxRef} className="w-full min-w-0">
      <svg width={width} height={HEIGHT} style={{ width: "100%" }} className="block select-none" role="img" aria-label="Gantt prévu et réel">
        <defs>
          <pattern id="geotab-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill={OFF_COLOR} />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#9a3412" strokeWidth="3" />
          </pattern>
        </defs>
        {hours.map((m) => (
          <g key={m}>
            <line x1={x(m)} y1={14} x2={x(m)} y2={HEIGHT - 4} stroke="#f1f5f9" />
            <text x={x(m)} y={11} fontSize={10} fill="#64748b" textAnchor="middle">{fmtTime(m)}</text>
          </g>
        ))}
        <text x={4} y={PLAN_Y + 17} fontSize={11} fontWeight={700} fill="#334155">Prévu</text>
        <text x={4} y={REAL_Y + 12} fontSize={11} fontWeight={700} fill="#334155">Réel</text>
        <text x={4} y={REAL_Y + 23} fontSize={9.5} fill="#64748b">Geotab</text>

        {(cmp?.sites || []).filter((s) => s.real_arrival != null).map((s) => (
          <line key={`c${s.idx}`} x1={x(s.plan_arrival)} y1={PLAN_Y + ROW_HEIGHT} x2={x(s.real_arrival)} y2={REAL_Y}
            stroke={lineColor(deltaTone(s.real_arrival - s.plan_arrival, tolerance))} strokeWidth={1.5} strokeDasharray="4 3" />
        ))}

        <line x1={x(plan.departure)} y1={PLAN_Y + 13} x2={x(plan.return_at)} y2={PLAN_Y + 13} stroke="#94a3b8" strokeWidth={2} />
        <Marker x={x(plan.departure)} y={PLAN_Y} fill="#334155" title="Départ prévu du dépôt" time={plan.departure} />
        <Marker x={x(plan.return_at)} y={PLAN_Y} fill="#334155" title="Retour prévu au dépôt" time={plan.return_at} />
        {plan.sites.map((site, i) => {
          const idx = i + 1;
          const missing = sitesByIdx[idx]?.match === "missing";
          return (
            <Block key={`p${idx}`} x={x(site.arrival)} y={PLAN_Y} width={Math.max(x(site.departure) - x(site.arrival), 4)} kind={site.kind}
              label={`${idx} · ${site.name}`} dashed={missing} stroke={missing ? "#c5524a" : undefined}
              hover={{ title: `${idx} · ${site.name}`, rows: siteRows(site, sitesByIdx[idx], tolerance) }} />
          );
        })}

        {cmp && (
          <>
            <line x1={x(cmp.real_departure)} y1={REAL_Y + 13} x2={x(cmp.end_at)} y2={REAL_Y + 13} stroke="#141414" strokeWidth={2} />
            <Marker x={x(cmp.real_departure)} y={REAL_Y} fill="#141414" title="Départ réel du dépôt" time={cmp.real_departure} />
            <Marker x={x(cmp.end_at)} y={REAL_Y} fill="#141414" title={cmp.return_delta != null ? "Retour réel au dépôt" : cmp.in_progress ? "Dernier arrêt connu" : "Fin de journée"} time={cmp.end_at} />
            {cmp.visits.filter((v) => !v.after_day && v.arr >= cmp.real_departure && v.arr < cmp.end_at).map((v, i) => {
              const w = Math.max(x(Math.min(v.dep, end)) - x(v.arr), 3);
              const site = v.site_idx ? sitesByIdx[v.site_idx] : null;
              if (site) {
                return (
                  <Block key={`r${i}`} x={x(v.arr)} y={REAL_Y} width={w} kind={site.kind} label={`${site.idx} · ${site.name}`}
                    dashed={site.match !== "ok"} stroke={site.match !== "ok" ? ADDRESS_COLOR : undefined}
                    hover={{ title: `${site.idx} · ${site.name}`, rows: [...siteRows(plan.sites[site.idx - 1] || {}, site, tolerance), placeRow(v.place)] }} />
                );
              }
              const depot = v.kind === "depot";
              const stopRows = [["Arrêt", `${fmtTime(v.arr)} → ${fmtTime(v.dep)} · ${fmtDuration(v.dwell)}`], placeRow(v.place) || ["Position", `${v.lat.toFixed(4)}, ${v.lon.toFixed(4)}`]];
              if (v.kind === "loading") {
                return <Block key={`r${i}`} x={x(v.arr)} y={REAL_Y} width={w} kind="loading" label={v.name}
                  hover={{ title: `Chargement ${v.name}`, rows: [...stopRows, ["Commandes", "Prévues en préchargement la veille"]] }} />;
              }
              if (v.dwell < 10 && !v.off) {
                return <Hover key={`r${i}`} title="Arrêt court" rows={stopRows}><rect x={x(v.arr)} y={REAL_Y + 9} width={Math.max(w, 2)} height={8} fill="#64748b" /></Hover>;
              }
              return (
                <Hover key={`r${i}`} title={v.switch ? "Changement de camion" : depot ? "Dépôt · hors plan" : "Arrêt hors plan"} interactive={v.off}
                  rows={[...stopRows, v.off && Number.isFinite(v.lat) && Number.isFinite(v.lon) && ["Carte", <MapLink lat={v.lat} lon={v.lon} color={OFF_COLOR} title={`Voir l’arrêt hors plan de ${fmtTime(v.arr)} dans Google Maps`}>Google Maps</MapLink>]]}>
                  <g className="cursor-default" tabIndex={v.off ? 0 : undefined} aria-label={v.off ? `Arrêt hors plan de ${fmtTime(v.arr)}` : undefined}>
                    <rect x={x(v.arr)} y={REAL_Y} width={w} height={ROW_HEIGHT} rx={4} fill={depot && (!v.off || v.switch) ? DEPOT_COLOR : "url(#geotab-hatch)"} stroke={OUTLINE} />
                    {w > 14 && <text x={x(v.arr) + w / 2} y={REAL_Y + 17} fontSize={11} fontWeight={800} fill="#fff" textAnchor="middle">{v.switch ? "⇄" : depot ? "D" : "!"}</text>}
                  </g>
                </Hover>
              );
            })}
          </>
        )}
      </svg>
    </div>
  );
}
